import { useCallback, useState } from 'react';

/**
 * Clipboard is the transport for every secret and command in this app, so it
 * gets a real control (not a div) and a fallback for browsers that refuse the
 * async clipboard API outside a secure context.
 */
export function CopyButton({
  value,
  label = 'Copy',
  copiedLabel = 'Copied',
  className = 'btn btn--ghost btn--small',
  title,
}: {
  value: string;
  label?: string;
  copiedLabel?: string;
  className?: string;
  title?: string;
}): JSX.Element {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');

  const copy = useCallback(async () => {
    const clipboard = typeof navigator === 'undefined' ? undefined : navigator.clipboard;
    try {
      if (clipboard) {
        await clipboard.writeText(value);
        setState('copied');
      } else {
        legacyCopy(value);
        setState('copied');
      }
    } catch {
      legacyCopy(value);
      setState(clipboard ? 'failed' : 'copied');
    }
    window.setTimeout(() => setState('idle'), 1800);
  }, [value]);

  return (
    <button type="button" className={className} onClick={() => void copy()} title={title}>
      {state === 'copied' ? copiedLabel : state === 'failed' ? 'Select & copy' : label}
    </button>
  );
}

function legacyCopy(value: string): void {
  const area = document.createElement('textarea');
  area.value = value;
  area.setAttribute('readonly', '');
  area.style.position = 'fixed';
  area.style.opacity = '0';
  document.body.append(area);
  area.select();
  try {
    document.execCommand('copy');
  } catch {
    /* nothing else to try: the value is selectable on screen anyway */
  }
  area.remove();
}